// src/gac/BulkDeleteArticlesPanel.js
// Extracted from GlobalActionCenter.js (GAC modularization, phase 1). No behaviour change.
import React, { useState, useRef } from 'react';
import axios from '../axiosInstance';
import '../GlobalActionCenterStyles.css';

const BulkDeleteArticlesPanel = ({
    executing,
    setExecuting,
    results,
    setResults,
    setErrors,
}) => {
    // Bulk Delete Articles specific state
    const [bulkDeleteConfig, setBulkDeleteConfig] = useState({
        delimiter: ',',   // ',', ';', '\t', '|'
        items: [],        // [{ companyCode, storeCode, articleId }]
        csvFileName: '',
        csvError: null,
    });
    const bulkDeleteCsvRef = useRef(null);

    // === Bulk Delete Articles handlers ===
    const resolveDelimiter = (d) => (d === '\\t' || d === 'tab') ? '\t' : d;

    const handleBulkDeleteCsvSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const text = event.target.result;
                const delim = resolveDelimiter(bulkDeleteConfig.delimiter);
                const lines = text.split(/\r?\n/).filter(l => l.trim());
                if (lines.length === 0) {
                    setBulkDeleteConfig(prev => ({ ...prev, items: [], csvFileName: file.name, csvError: 'CSV is empty' }));
                    return;
                }

                const firstLower = lines[0].toLowerCase();
                const hasHeader = firstLower.includes('company') || firstLower.includes('store') || firstLower.includes('product') || firstLower.includes('article');
                const dataLines = hasHeader ? lines.slice(1) : lines;

                const parsed = [];
                for (let i = 0; i < dataLines.length; i++) {
                    const line = dataLines[i].trim();
                    if (!line) continue;
                    const parts = line.split(delim).map(p => p.trim().replace(/^"|"$/g, ''));
                    if (parts.length < 3) {
                        setBulkDeleteConfig(prev => ({
                            ...prev,
                            items: [],
                            csvFileName: file.name,
                            csvError: `Line ${i + (hasHeader ? 2 : 1)} has fewer than 3 columns (company, store, product id)`
                        }));
                        return;
                    }
                    const [companyCode, storeCode, articleId] = parts;
                    if (!companyCode || !storeCode || !articleId) {
                        setBulkDeleteConfig(prev => ({
                            ...prev,
                            items: [],
                            csvFileName: file.name,
                            csvError: `Line ${i + (hasHeader ? 2 : 1)} has empty values`
                        }));
                        return;
                    }
                    parsed.push({
                        companyCode: companyCode.toUpperCase(),
                        storeCode: storeCode.toUpperCase(),
                        articleId: articleId
                    });
                }

                if (parsed.length === 0) {
                    setBulkDeleteConfig(prev => ({ ...prev, items: [], csvFileName: file.name, csvError: 'No valid rows found in CSV' }));
                    return;
                }

                setBulkDeleteConfig(prev => ({ ...prev, items: parsed, csvFileName: file.name, csvError: null }));
            } catch (err) {
                setBulkDeleteConfig(prev => ({ ...prev, items: [], csvError: 'Failed to parse CSV: ' + err.message }));
            }
        };
        reader.readAsText(file);
        e.target.value = ''; // allow re-upload of the same file
    };

    const handleDownloadBulkDeleteSampleCsv = () => {
        const d = resolveDelimiter(bulkDeleteConfig.delimiter);
        const rows = [
            ['companycode', 'storecode', 'productid'],
            ['E89', 'EO89', 'B1020001'],
            ['E89', 'EO89', 'B1020002'],
            ['E89', 'EO90', 'B1020003'],
        ];
        const sample = rows.map(r => r.join(d)).join('\n');
        const blob = new Blob([sample], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'BulkDeleteArticles_Sample.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const executeBulkDeleteArticles = async () => {
        const items = bulkDeleteConfig.items;
        if (!items || items.length === 0) {
            alert('Please upload a CSV with at least one row first.');
            return;
        }
        const groupCount = new Set(items.map(i => `${i.companyCode}:${i.storeCode}`)).size;
        if (!window.confirm(`DELETE ${items.length} article(s) across ${groupCount} store(s)? This cannot be undone.`)) {
            return;
        }

        setExecuting(true);
        setResults(null);
        setErrors([]);
        try {
            const response = await axios.post('global-action/actions/bulk-delete-articles', { items });
            setResults(response.data);
            if (!response.data?.success) {
                setErrors([`${response.data?.summary?.totalFailed ?? 0} article(s) failed to delete`]);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message;
            setErrors(['Bulk delete failed: ' + errorMsg]);
            if (error.response?.data) setResults(error.response.data);
        } finally {
            setExecuting(false);
        }
    };

    return (
        <>
            {/* Bulk Delete Articles Configuration */}
            <div className="blink-led-container bulk-delete-container">
                <div className="action-config-section blink-led-section">
                    <h3>🗑️ Bulk Delete Articles</h3>
                    <div className="config-panel">
                        <p className="csv-hint" style={{ marginBottom: 12 }}>
                            Upload a CSV to bulk-delete articles from SOLUM. Each row must contain <code>company code</code>, <code>store code</code>, and <code>product id</code>. Rows are grouped by company+store, and one delete call is issued per group.
                        </p>

                        <div className="config-row">
                            <label>CSV Delimiter:</label>
                            <select
                                className="label-type-select"
                                value={bulkDeleteConfig.delimiter}
                                onChange={(e) => setBulkDeleteConfig(prev => ({ ...prev, delimiter: e.target.value }))}
                            >
                                <option value=",">Comma  ( , )</option>
                                <option value=";">Semicolon  ( ; )</option>
                                <option value="\t">Tab</option>
                                <option value="|">Pipe  ( | )</option>
                            </select>
                        </div>

                        <div className="config-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                            <div style={{ marginBottom: 8, fontSize: '0.9em', color: '#555' }}>
                                Example CSV (delimiter = <code>{bulkDeleteConfig.delimiter === '\\t' ? 'Tab' : bulkDeleteConfig.delimiter}</code>):
                            </div>
                            <pre style={{
                                background: '#f5f5f5',
                                padding: '8px 12px',
                                borderRadius: 4,
                                fontSize: '0.85em',
                                margin: 0,
                                width: '100%',
                                overflowX: 'auto'
                            }}>{(() => {
                                const d = resolveDelimiter(bulkDeleteConfig.delimiter);
                                return [
                                    ['companycode', 'storecode', 'productid'].join(d),
                                    ['E89', 'EO89', 'B1020001'].join(d),
                                    ['E89', 'EO89', 'B1020002'].join(d),
                                    ['E89', 'EO90', 'B1020003'].join(d),
                                ].join('\n');
                            })()}</pre>
                            <button
                                className="csv-upload-btn"
                                style={{ marginTop: 8 }}
                                onClick={handleDownloadBulkDeleteSampleCsv}
                            >
                                📥 Download Sample CSV
                            </button>
                        </div>

                        <div className="config-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                            <input
                                type="file"
                                ref={bulkDeleteCsvRef}
                                accept=".csv,.txt"
                                onChange={handleBulkDeleteCsvSelect}
                                style={{ display: 'none' }}
                            />
                            <button
                                className="csv-upload-btn"
                                onClick={() => bulkDeleteCsvRef.current?.click()}
                            >
                                Choose CSV File
                            </button>
                            {bulkDeleteConfig.csvFileName && (
                                <div className="csv-loaded-info" style={{ marginTop: 6 }}>
                                    <strong>{bulkDeleteConfig.csvFileName}</strong>
                                    {bulkDeleteConfig.items.length > 0 && (
                                        <> — Loaded {bulkDeleteConfig.items.length.toLocaleString()} row(s) across {new Set(bulkDeleteConfig.items.map(i => `${i.companyCode}:${i.storeCode}`)).size} store group(s)</>
                                    )}
                                </div>
                            )}
                            {bulkDeleteConfig.csvError && (
                                <div className="buu-csv-error" style={{ color: '#b00020', marginTop: 6 }}>{bulkDeleteConfig.csvError}</div>
                            )}
                        </div>

                        {bulkDeleteConfig.items.length > 0 && (
                            <div className="config-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                                <div style={{ fontSize: '0.9em', marginBottom: 6 }}>Preview (first 10 rows):</div>
                                <table className="buu-table" style={{ width: '100%', fontSize: '0.9em' }}>
                                    <thead>
                                        <tr>
                                            <th>Company</th>
                                            <th>Store</th>
                                            <th>Article ID</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bulkDeleteConfig.items.slice(0, 10).map((item, idx) => (
                                            <tr key={idx}>
                                                <td>{item.companyCode}</td>
                                                <td>{item.storeCode}</td>
                                                <td>{item.articleId}</td>
                                            </tr>
                                        ))}
                                        {bulkDeleteConfig.items.length > 10 && (
                                            <tr>
                                                <td colSpan={3} className="buu-more" style={{ textAlign: 'center', fontStyle: 'italic' }}>
                                                    ...and {bulkDeleteConfig.items.length - 10} more
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className="config-row">
                            <button
                                className="execute-btn"
                                style={{ background: '#b00020', color: '#fff' }}
                                onClick={executeBulkDeleteArticles}
                                disabled={executing || bulkDeleteConfig.items.length === 0}
                            >
                                {executing ? 'Deleting...' : `Delete ${bulkDeleteConfig.items.length.toLocaleString()} Article(s)`}
                            </button>
                            {bulkDeleteConfig.items.length > 0 && (
                                <button
                                    className="csv-upload-btn"
                                    style={{ marginLeft: 8 }}
                                    onClick={() => setBulkDeleteConfig(prev => ({ ...prev, items: [], csvFileName: '', csvError: null }))}
                                    disabled={executing}
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="action-config-section blink-led-results-section">
                    <h3>Results</h3>
                    <div className="config-panel">
                        {results && results.summary ? (
                            <>
                                <div className="results-summary blink-led-results">
                                    <div className="summary-card info">
                                        <div className="summary-label">Articles Requested</div>
                                        <div className="summary-value">{results.summary.totalItems?.toLocaleString()}</div>
                                    </div>
                                    <div className="summary-card success">
                                        <div className="summary-label">Deleted</div>
                                        <div className="summary-value">{results.summary.totalDeleted?.toLocaleString()}</div>
                                    </div>
                                    <div className={`summary-card ${results.summary.totalFailed > 0 ? 'error' : 'success'}`}>
                                        <div className="summary-label">Failed</div>
                                        <div className="summary-value">{results.summary.totalFailed?.toLocaleString()}</div>
                                    </div>
                                </div>

                                {results.groups && results.groups.length > 0 && (
                                    <table className="buu-table" style={{ width: '100%', fontSize: '0.9em', marginTop: 12 }}>
                                        <thead>
                                            <tr>
                                                <th>Company</th>
                                                <th>Store</th>
                                                <th>Requested</th>
                                                <th>Status</th>
                                                <th>Message</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {results.groups.map((g, idx) => (
                                                <tr key={idx}>
                                                    <td>{g.companyCode}</td>
                                                    <td>{g.storeCode}</td>
                                                    <td>{g.requested}</td>
                                                    <td style={{ color: g.status === 'success' ? '#0a7f2e' : '#b00020' }}>
                                                        {g.status === 'success' ? '✓' : '✗'} {g.status}
                                                    </td>
                                                    <td>{g.message}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </>
                        ) : (
                            <div className="no-results-placeholder">
                                <p>Results will appear here after deleting articles.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default BulkDeleteArticlesPanel;
